import React, { useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import type { FieldValues, UseControllerProps } from "react-hook-form";
import type { SubscriptionWithSerial } from "client/api-v2";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { getSubscriptions } from "../../services/subscriptionService";

type SubscriptionSelectorProps<T extends FieldValues> = UseControllerProps<T> & {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  currentAccountSubscriptionIds?: ReadonlySet<string>;
};

type SubscriptionLinkStatus = "notLinked" | "linkedToThisAccount" | "linkedToAnotherAccount";

export interface SubscriptionOption {
  id: string;
  serial: string;
  friendlyName: string | null;
  linkStatus: SubscriptionLinkStatus;
}

const defaultListPageSize = 10;
const searchPageSize = 25;
const noCurrentAccountSubscriptionIds: ReadonlySet<string> = new Set();

function resolveLinkStatus(
  subscription: SubscriptionWithSerial,
  currentAccountSubscriptionIds: ReadonlySet<string>
): SubscriptionLinkStatus {
  if (!subscription.subscription.billingAccountId) {
    return "notLinked";
  }
  return currentAccountSubscriptionIds.has(subscription.id) ? "linkedToThisAccount" : "linkedToAnotherAccount";
}

function toOption(
  subscription: SubscriptionWithSerial,
  currentAccountSubscriptionIds: ReadonlySet<string>
): SubscriptionOption {
  return {
    id: subscription.id,
    serial: subscription.serial,
    friendlyName: subscription.subscription.friendlyName ?? null,
    linkStatus: resolveLinkStatus(subscription, currentAccountSubscriptionIds),
  };
}

function makeSearchSubscriptions(currentAccountSubscriptionIds: ReadonlySet<string>) {
  return (searchTerm?: string) =>
    getSubscriptions(
      searchTerm ? { serial: searchTerm, pageSize: searchPageSize } : { pageSize: defaultListPageSize },
      new AbortController().signal
    ).then((response) => {
      const entities = response.items.map((item) => toOption(item, currentAccountSubscriptionIds));
      return { entities, count: entities.length, totalCount: response.totalItems };
    });
}

function noSearch() {
  return Promise.resolve({ entities: [], count: 0, totalCount: 0 });
}

function formatSubscriptionName(subscription: SubscriptionOption): string {
  return subscription.friendlyName ? `${subscription.friendlyName} (${subscription.serial})` : subscription.serial;
}

function renderSubscriptionOption(subscription: SubscriptionOption) {
  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="body2">{subscription.friendlyName ?? subscription.serial}</Typography>
        {subscription.linkStatus === "linkedToAnotherAccount" && (
          <Chip label="Linked" size="small" variant="filled" color="default" />
        )}
        {subscription.linkStatus === "linkedToThisAccount" && (
          <Chip label="On this account" size="small" variant="filled" color="primary" />
        )}
      </Stack>
      {subscription.friendlyName && (
        <Typography variant="caption" color="text.secondary">
          {subscription.serial}
        </Typography>
      )}
    </Box>
  );
}

function renderSelectorFooter(): React.ReactNode {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Type to search and see more results
      </Typography>
    </Box>
  );
}

export function SubscriptionSelector<T extends FieldValues>(props: SubscriptionSelectorProps<T>) {
  const { label, required, disabled, currentAccountSubscriptionIds = noCurrentAccountSubscriptionIds, ...rest } = props;
  const searchSubscriptions = useMemo(
    () => makeSearchSubscriptions(currentAccountSubscriptionIds),
    [currentAccountSubscriptionIds]
  );

  return (
    <EntitySelector
      {...rest}
      disabled={disabled}
      label={label || "Subscription"}
      apiRequest={disabled ? noSearch : searchSubscriptions}
      optionLabelFunc={formatSubscriptionName}
      renderOption={renderSubscriptionOption}
      required={required}
      renderFooter={renderSelectorFooter}
    />
  );
}
